'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { MicroWeeklyReadBody } from '@akademi/site-builder-runtime';
import type { RendererProps } from '../types';
import { PopupHeader, padded } from '../chrome';
import { RichHtmlContent } from '../../../_shared/RichHtmlContent';

/**
 * Long-form reading renderer over the canonical
 * {@code MicroWeeklyReadBody}: inner title, summary lede, inline image,
 * the main {@code body}, and the opt-in case-study {@code structure}
 * sections. The body is operator-authored HTML when
 * {@code bodyFormat === 'html'} — rendered through the shared
 * {@link RichHtmlContent}, so it picks up the same typography as the
 * RichText block, with the admin editor as the trust boundary. Rows written before that editor
 * carry no format flag and keep the basic {@code #}/{@code ##} markdown
 * path. A top progress bar
 * follows scroll depth; ≥90% scroll fires {@code onComplete}. Content
 * that fits without scrolling can never hit that threshold, so
 * fits-at-mount counts as read.
 */
export function WeeklyReadRenderer({ item, onComplete }: RendererProps) {
  const body = (item.body ?? {}) as MicroWeeklyReadBody;
  const { t } = useTranslation('site-builder-blocks');
  const ref = useRef<HTMLDivElement | null>(null);
  const [progress, setProgress] = useState(0);
  const [done, setDone] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onScroll = () => {
      const total = el.scrollHeight - el.clientHeight;
      if (total <= 0) return;
      const pct = Math.min(100, Math.round((el.scrollTop / total) * 100));
      setProgress(pct);
      if (!done && pct >= 90) {
        setDone(true);
        onComplete();
      }
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [done, onComplete]);

  // Fits-without-scroll = read. Checked once the content is mounted;
  // without this the scroll handler above never reaches completion.
  useEffect(() => {
    const el = ref.current;
    if (!el || done) return;
    if (el.scrollHeight <= el.clientHeight) {
      setProgress(100);
      setDone(true);
      onComplete();
    }
  }, [done, onComplete]);

  // `bodyFormat: 'html'` is what the admin's WYSIWYG editor writes. Rows
  // without it predate that editor and stay on the plain-text path.
  const isHtml = body.bodyFormat === 'html';
  const paragraphs = isHtml ? [] : (body.body ?? '').split(/\n\s*\n/).filter(Boolean);
  const structure = body.structure;
  const sections = structure
    ? [
        { label: t('microWeeklyRead.structure.situation'), text: structure.situation },
        { label: t('microWeeklyRead.structure.complication'), text: structure.complication },
        { label: t('microWeeklyRead.structure.resolution'), text: structure.resolution },
        { label: t('microWeeklyRead.structure.takeaway'), text: structure.takeaway },
      ].filter((s) => s.text)
    : [];

  return (
    <>
      <div
        style={{
          height: 3,
          background: 'var(--color-neutral-100, #f4f4f5)',
          position: 'sticky',
          top: 0,
          zIndex: 2,
        }}
      >
        <div
          style={{
            width: `${progress}%`,
            height: '100%',
            background: 'var(--color-brand-500, #3b5bdb)',
            transition: 'width 120ms ease',
          }}
        />
      </div>
      <div ref={ref} style={{ maxHeight: '70vh', overflowY: 'auto' }}>
        <PopupHeader kind={item.kind} title={item.title} />
        <article
          style={{
            ...padded,
            paddingTop: 0,
            fontSize: 'var(--font-size-md, 16px)',
            lineHeight: 1.7,
            color: 'var(--color-text-primary, #18181b)',
          }}
        >
          {body.innerTitle && (
            <h2
              style={{
                margin: '0 0 12px',
                fontSize: 'var(--font-size-xl, 22px)',
                fontWeight: 700,
                lineHeight: 1.25,
              }}
            >
              {body.innerTitle}
            </h2>
          )}
          {body.summary && (
            <blockquote
              style={{
                margin: '0 0 20px',
                padding: '12px 16px',
                borderInlineStart: '3px solid var(--color-brand-500, #3b5bdb)',
                background: 'var(--color-surface-page, #f8fafc)',
                borderRadius: '0 8px 8px 0',
                fontSize: 'var(--font-size-sm, 14px)',
                color: 'var(--color-text-secondary, #71717a)',
              }}
            >
              {body.summary}
            </blockquote>
          )}
          {body.imageUrl && (
            <img
              src={body.imageUrl}
              alt=""
              style={{ width: '100%', borderRadius: 12, marginBottom: 20, objectFit: 'cover' }}
            />
          )}
          {isHtml ? <RichHtmlContent html={body.body ?? ''} /> : null}
          {paragraphs.map((p, i) => {
            if (p.startsWith('## '))
              return (
                <h3
                  key={i}
                  style={{ fontSize: 'var(--font-size-lg, 18px)', fontWeight: 600, marginTop: 24 }}
                >
                  {p.slice(3)}
                </h3>
              );
            if (p.startsWith('# '))
              return (
                <h2
                  key={i}
                  style={{ fontSize: 'var(--font-size-xl, 22px)', fontWeight: 700, marginTop: 24 }}
                >
                  {p.slice(2)}
                </h2>
              );
            return <p key={i}>{p}</p>;
          })}
          {sections.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14, marginTop: 24 }}>
              {sections.map((s) => (
                <section
                  key={s.label}
                  style={{
                    padding: '14px 18px',
                    borderRadius: 12,
                    border: '1px solid var(--color-border, rgba(0,0,0,0.08))',
                    background: 'var(--color-surface-card, #fff)',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs, 12px)',
                      fontWeight: 700,
                      letterSpacing: 'var(--font-tracking-wider, 0.04em)',
                      textTransform: 'uppercase',
                      color: 'var(--color-brand-500, #3b5bdb)',
                      marginBottom: 6,
                    }}
                  >
                    {s.label}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-sm, 14px)', lineHeight: 1.65 }}>
                    {s.text}
                  </div>
                </section>
              ))}
            </div>
          )}
        </article>
      </div>
    </>
  );
}
